import {useEffect, useRef, useCallback} from 'react';
import {InfiniteData, useQueryClient} from '@tanstack/react-query';
import {Client, IMessage} from '@stomp/stompjs';
import {useChatContext} from '../context';
import {createStompChatClient} from './stompChatClient';
import {Chat, ChatMessage} from '@services/Chat/types';
import {PaginatedResponse} from 'types/pagination';

function isWebSocketOpen(client: Client | null): boolean {
    const socket = (client as any)?._transport?.socket ?? (client as any)?.webSocket;
    return socket?.readyState === WebSocket.OPEN;
}

export const useStompChat = () => {
    const {selectedChat, setStompClient, currentUser, updateUnreadCount} = useChatContext();
    const queryClient = useQueryClient();

    const clientRef = useRef<Client | null>(null);
    const notifSubRef = useRef<{unsubscribe: () => void}>();
    const chatSubRef = useRef<{unsubscribe: () => void}>();
    const selectedChatIdRef = useRef<string | null>(null);

    useEffect(() => {
        selectedChatIdRef.current = selectedChat?.id ?? null;
    }, [selectedChat?.id]);

    const handlePersonalMessage = useCallback(
        (msg: IMessage) => {
            if (!msg.body) return;
            const data = JSON.parse(msg.body) as Chat;
            const cached = queryClient.getQueryData<Chat[]>(['chats']) ?? [];
            const exists = cached.some((c) => c.id === data.id);
            if (exists) {
                updateUnreadCount({id: data.id, unreadMessages: data.unreadMessages});
            } else {
                queryClient.invalidateQueries({queryKey: ['chats']});
            }
        },
        [queryClient, updateUnreadCount],
    );

    const handleChatMessage = useCallback(
        (msg: IMessage) => {
            if (!msg.body || !selectedChat) return;
            const data = JSON.parse(msg.body) as ChatMessage;

            queryClient.setQueryData<InfiniteData<PaginatedResponse<ChatMessage[]>>>(
                ['chatHistory', selectedChat.id],
                (old) => {
                    if (!old) return old;
                    const pages = old.pages.map((p, i) =>
                        i === 0 ? {...p, content: [...p.content, data]} : p,
                    );
                    return {...old, pages};
                },
            );

            if (data.fromUserId !== currentUser?.username) {
                queryClient.setQueryData<PaginatedResponse<ChatMessage[]>>(
                    ['chatUnread', selectedChat.id],
                    (old) => {
                        if (!old) return old;
                        if (old.content.some((m) => m.id === data.id)) return old;
                        return {...old, content: [...old.content, data]};
                    },
                );
            }
        },
        [selectedChat, currentUser, queryClient],
    );

    const subscribeToNotifications = useCallback(
        (client: Client) => {
            if (!currentUser?.username) return;
            try {
                notifSubRef.current?.unsubscribe?.();
            } catch {}
            notifSubRef.current = client.subscribe(
                `/user/${currentUser.username}/queue/messages`,
                handlePersonalMessage,
            );
        },
        [currentUser?.username, handlePersonalMessage],
    );

    const subscribeChat = useCallback(
        (client: Client, chatId: string) => {
            try {
                chatSubRef.current?.unsubscribe?.();
            } catch {}
            chatSubRef.current = client.subscribe(
                `/topic/chat/messages/${chatId}`,
                handleChatMessage,
            );
        },
        [handleChatMessage],
    );

    useEffect(() => {
        const client = createStompChatClient();
        clientRef.current = client;
        setStompClient(client);

        client.onConnect = () => {
            // пересоздаём подписки после успешного соединения
            subscribeToNotifications(client);
            const id = selectedChatIdRef.current;
            if (id) subscribeChat(client, id);
        };

        // при разрыве/отключении очищаем локальные ссылки на подписки
        // чтобы на следующем onConnect мы точно пересоздали их
        const clearSubs = () => {
            notifSubRef.current = undefined;
            chatSubRef.current = undefined;
        };
        client.onDisconnect = clearSubs;
        (client).onWebSocketClose = clearSubs;

        client.activate();

        return () => {
            if (isWebSocketOpen(client)) {
                try {
                    notifSubRef.current?.unsubscribe();
                } catch {}
                try {
                    chatSubRef.current?.unsubscribe();
                } catch {}
            }
            client.deactivate();
            setStompClient(null);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setStompClient, subscribeToNotifications]);

    // переподписка при переключении чата 
    useEffect(() => {
        const client = clientRef.current;
        if (!client?.connected || !selectedChat?.id) return;
        subscribeChat(client, selectedChat.id);
    }, [selectedChat?.id, subscribeChat]);
};
